/* collins-immersive.js: the immersive service page (STOP G, 18 Oct 2026; 14islands.com as the motion reference,
   COLLINS as the base; research/14islands/MOTION-14ISLANDS.md). Loaded only on the pages the cl-service section lists
   in its "immersive" setting, after gsap, ScrollTrigger and collins-home.js (which keeps the window grow, the ground
   switch and the card clip-in; nothing here touches them).
     - the h1 words rise out of depth once on load: pure CSS (cl-service.liquid), so the first paint is already in the
       start pose and nothing waits for this file
     - the h1 recedes (back in depth, tipped back) while the window grows: scrubbed over the same range, the first
       screen of scrolling; intent: the title steps back as the work comes forward
     - each h2 rises in depth once as it enters (words split here, text nodes only; links and tags stay as they are);
       intent: each section announces itself in reading order, once
     - the window loop (real Al Wafaa work, Remotion): its source is attached after the page's load event, only while
       the window is on screen; it plays only on screen and the viewer's pause always wins. Reduced motion and
       Save-Data keep the poster (the page's own picture, which is the loop's first frame) and show no button.
     - STOP I (19 Oct 2026): every service and city page has its own loop, and a case listed in the section's "reels"
       setting plays its loop inside its card: attached after the load event, only while the card is half on screen,
       paused off screen, with its own pause (the viewer's pause wins). The card's own picture is the reel's first
       frame, so nothing jumps when it starts. Reduced motion and Save-Data keep the picture and show no button.
     - STOP J (19 Oct 2026): every case card is a reel. Reels that come into view together first start in reading
       order, 0.67 s apart (a third of an act), so a row of three ripples instead of changing in step; each card
       shows its own picture until its turn. A reel coming into view on its own starts at once; a resume is at once.
       On a card under 170 px wide the pill reads Pause / Play (its spoken name stays the full phrase), so it never
       wraps over a narrow portrait card on a phone.
   Transforms only, never opacity, so the LCP never moves. Native scroll, no pin, no smooth-scroll layer.
   Reduced motion: no split, no trigger, no video (also when it is switched on while the page is open).
   Teardown on pagehide and shopify:section:unload. */
(function () {
  'use strict';
  var d = document, de = d.documentElement, w = window;
  var mqReduce = w.matchMedia('(prefers-reduced-motion: reduce)');
  var S = null;

  function on(t, type, fn, opt) { t.addEventListener(type, fn, opt); S.off.push(function () { t.removeEventListener(type, fn, opt); }); }

  /* ---------- the window loop ---------- */
  function loop() {
    var v = d.querySelector('[data-im-video]'), b = d.querySelector('[data-im-pause]');
    if (!v || !b) return;
    var save = !!(navigator.connection && navigator.connection.saveData);
    if (mqReduce.matches || save) return;                                   /* the poster only, and no button */
    var phone = w.matchMedia('(max-width: 767px)').matches;
    var src = phone ? (v.getAttribute('data-p') || v.getAttribute('data-d')) : v.getAttribute('data-d');
    if (!src) return;
    var userPaused = false, visible = false, loaded = d.readyState === 'complete', attached = false;
    var label = function () { b.textContent = v.paused ? 'Play the loop' : 'Pause the loop'; };
    var play = function () {
      if (!attached) { attached = true; v.src = src; v.preload = 'auto'; v.load(); }
      var p = v.play(); if (p && p.catch) p.catch(function () {});
    };
    /* the opening sheet (cl-press) holds the page's own load-in; the loop starts as it lifts, on its first frame */
    var held = function () { return de.classList.contains('cl-press-hold'); };
    var go = function () { if (visible && loaded && !userPaused && !held()) play(); };
    if (held() && 'MutationObserver' in w) {
      var mo = new MutationObserver(function () { if (!held()) { mo.disconnect(); go(); } });
      mo.observe(de, { attributes: true, attributeFilter: ['class'] });
      S.off.push(function () { mo.disconnect(); });
    }
    on(v, 'playing', function () {
      v.classList.add('is-on'); b.hidden = false; label();                  /* the pause appears the moment anything moves */
      if (!b.style.minWidth) {                                               /* prompt 4: sized once to the longer label (no layout shift) */
        b.textContent = 'Pause the loop'; b.style.minWidth = (b.offsetWidth / parseFloat(w.getComputedStyle(b).fontSize)).toFixed(3) + 'em'; label();
      }
    });
    on(v, 'pause', label);
    on(b, 'click', function () {
      if (v.paused) { userPaused = false; play(); } else { userPaused = true; v.pause(); }
      label();
    });
    if (!loaded) on(w, 'load', function () { loaded = true; go(); });
    if ('IntersectionObserver' in w) {
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          visible = e.isIntersecting;
          if (!visible) { if (!v.paused) v.pause(); }
          else go();
        });
      }, { threshold: 0.15 });
      io.observe(v);
      S.off.push(function () { io.disconnect(); });
    } else { visible = true; go(); }
    S.off.push(function () { try { v.pause(); } catch (e) {} v.classList.remove('is-on'); b.hidden = true; });   /* back to the page's own picture */
  }

  /* ---------- the case reels (STOP I): one loop per card, each on its own ---------- */
  function reels() {
    var vs = Array.prototype.slice.call(d.querySelectorAll('[data-im-reel]'));
    if (!vs.length) return;
    var save = !!(navigator.connection && navigator.connection.saveData);
    if (mqReduce.matches || save) return;                                   /* the card's picture only, and no button */
    var loaded = d.readyState === 'complete';
    var all = [], lastFirst = -1e9, GAP = 667;      /* first starts in reading order, 0.67 s apart */
    /* reels that come into view in the same frame are slotted in page order (visibility callbacks arrive in any order) */
    var pend = [], fq = 0;
    var flush = function () { fq = 0; pend.sort(function (a, b) { return a.i - b.i; }); var p = pend; pend = []; p.forEach(function (r) { r.slot(); }); };
    vs.forEach(function (v, vi) {
      var card = v.closest('.cl-card'), b = card && card.querySelector('[data-im-reel-pause]'), src = v.getAttribute('data-src');
      if (!b || !src) return;
      var r = { i: vi, userPaused: false, visible: false, attached: false, started: false, timer: 0 };
      var label = function () {                     /* a narrow card (a portrait card on a phone) gets the short label */
        var p = v.paused, short = card.getBoundingClientRect().width < 170;
        b.textContent = short ? (p ? 'Play' : 'Pause') : (p ? 'Play the reel' : 'Pause the reel');
        b.classList.toggle('is-short', short);
        if (short) b.setAttribute('aria-label', p ? 'Play the reel' : 'Pause the reel'); else b.removeAttribute('aria-label');
      };
      var attach = function () { if (!r.attached) { r.attached = true; v.src = src; v.preload = 'auto'; v.load(); } };
      var play = function () {
        attach();
        var p = v.play(); if (p && p.catch) p.catch(function () {});
      };
      r.go = function () {
        if (!(r.visible && loaded && !r.userPaused && !de.classList.contains('cl-press-hold'))) return;
        if (r.started) { play(); return; }
        if (r.timer || pend.indexOf(r) > -1) return;
        pend.push(r); if (!fq) fq = w.requestAnimationFrame(flush);
      };
      r.slot = function () {
        if (!r.visible || r.timer || r.started) return;
        var now = Date.now(), at = Math.max(now, lastFirst + GAP);
        lastFirst = at; attach();
        r.timer = setTimeout(function () { r.timer = 0; if (r.visible && !r.userPaused) { r.started = true; play(); } }, at - now);
      };
      on(v, 'playing', function () { v.classList.add('is-on'); b.hidden = false; label(); });
      on(v, 'pause', label);
      /* a press on the pill is not a drag of the track or a click on the card's link */
      on(b, 'pointerdown', function (e) { e.stopPropagation(); });
      on(b, 'click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (v.paused) { r.userPaused = false; r.started = true; play(); } else { r.userPaused = true; v.pause(); }
        label();
      });
      if ('IntersectionObserver' in w) {
        var io = new IntersectionObserver(function (es) {
          es.forEach(function (e) { r.visible = e.isIntersecting; if (!r.visible) { clearTimeout(r.timer); r.timer = 0; if (!v.paused) v.pause(); } else r.go(); });
        }, { threshold: 0.5 });
        io.observe(card);
        S.off.push(function () { io.disconnect(); });
      } else { r.visible = true; }
      S.off.push(function () { clearTimeout(r.timer); r.timer = 0; try { v.pause(); } catch (e) {} v.classList.remove('is-on'); b.hidden = true; });
      all.push(r);
    });
    S.off.push(function () { if (fq) w.cancelAnimationFrame(fq); fq = 0; pend = []; });
    var go = function () { all.forEach(function (r) { r.go(); }); };
    if (!loaded) on(w, 'load', function () { loaded = true; go(); }); else go();
  }

  /* ---------- words: split a heading's text nodes into inline-block words, keeping every space and tag ---------- */
  function split(h) {
    var walker = d.createTreeWalker(h, NodeFilter.SHOW_TEXT, null), nodes = [], n, i = 0;
    while ((n = walker.nextNode())) nodes.push(n);
    nodes.forEach(function (t) {
      var parts = t.nodeValue.split(/(\s+)/), frag = d.createDocumentFragment();
      parts.forEach(function (p) {
        if (!p) return;
        if (/^\s+$/.test(p)) { frag.appendChild(d.createTextNode(p)); return; }
        var s = d.createElement('span'); s.className = 'im-w'; s.style.setProperty('--i', i++); s.textContent = p;
        frag.appendChild(s);
      });
      t.parentNode.replaceChild(frag, t);
    });
    h.classList.add('im-h2');
    return function () {                                                      /* undo: the heading's own text back */
      h.querySelectorAll('.im-w').forEach(function (s) { s.replaceWith(d.createTextNode(s.textContent)); });
      h.normalize(); h.classList.remove('im-h2', 'im-pre', 'im-go');
    };
  }

  /* ---------- the type in depth (scroll) ---------- */
  function type() {
    if (mqReduce.matches || !w.gsap || !w.ScrollTrigger) return;
    var gsap = w.gsap, ST = w.ScrollTrigger;
    gsap.registerPlugin(ST);
    var made = [], undo = [];
    var h1 = d.querySelector('.im-h1');
    if (h1) {
      /* the same range as collins-home.js's window grow: the body's top to one screen less the header's 60 px */
      /* STOP Q (26 Oct 2026, the owner's yes on call 2): the perspective stands at 1200 from the first pixel. Tweened
         from none with z, it made the depth scale 1200/1340 on the first scrolled frame: the heading dropped about 10% */
      made.push(gsap.fromTo(h1, { z: 0, rotationX: 0, y: 0, transformPerspective: 1200 }, {
        z: -140, rotationX: 10, y: -24, transformPerspective: 1200, transformOrigin: '50% 100%', ease: 'none', immediateRender: false,
        scrollTrigger: { trigger: d.body, start: 'top top', end: function () { return '+=' + Math.max(1, w.innerHeight - 60); }, scrub: true, invalidateOnRefresh: true }
      }));
    }
    var vh = w.innerHeight;
    Array.prototype.slice.call(d.querySelectorAll('.cl-svc h2')).forEach(function (h) {
      if (h.getBoundingClientRect().top < vh * 0.88) return;                  /* already in view: it stays at rest */
      undo.push(split(h));
      h.classList.add('im-pre');
      made.push(ST.create({
        trigger: h, start: 'top 88%', once: true,
        onEnter: function () { h.classList.add('im-go'); h.classList.remove('im-pre'); }
      }));
    });
    var refresh = function () { ST.refresh(); };
    on(w, 'load', refresh);
    if (d.fonts && d.fonts.ready) d.fonts.ready.then(function () { if (S) ST.refresh(); });
    S.off.push(function () {
      made.forEach(function (m) { if (m.scrollTrigger) m.scrollTrigger.kill(); if (m.kill) m.kill(); });
      if (h1) gsap.set(h1, { clearProps: 'transform,transformPerspective' });
      undo.forEach(function (u) { u(); });
    });
  }

  function init() {
    if (S || !d.querySelector('.cl-svc .im-h1, [data-im-video], [data-im-reel]')) return;
    S = { off: [] };
    loop(); reels(); type();
  }
  function destroy() {
    if (!S) return;
    var off = S.off; S = null;
    for (var i = off.length - 1; i >= 0; i--) { try { off[i](); } catch (e) {} }
  }

  var ours = function (e) { return e.target && e.target.querySelector && e.target.querySelector('.cl-svc'); };
  d.addEventListener('shopify:section:unload', function (e) { if (ours(e)) destroy(); });
  d.addEventListener('shopify:section:load', function (e) { if (ours(e)) { destroy(); init(); } });
  var mqChange = function () { destroy(); if (!mqReduce.matches) init(); };   /* reduced motion switched on or off */
  if (mqReduce.addEventListener) mqReduce.addEventListener('change', mqChange); else if (mqReduce.addListener) mqReduce.addListener(mqChange);
  w.addEventListener('pagehide', destroy);
  w.addEventListener('pageshow', function (e) { if (e.persisted) init(); });
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init); else init();
})();
